<script lang="ts" setup>
import PageHeader from "@/components/PageHeader.vue";
import DataView from "@/components/DataView.vue";
import { ref, computed, onMounted, onActivated } from "vue";
import type { Config } from "datatables.net";
import { useRouter } from "vue-router";
import AddSubscriberWithProductModal from "@/components/AddSubscriberWithProductModal.vue";
import { subscriptionsApi } from "@/services/subscriptions";
import { useProducts } from "@/queries/products";
import { useSessionStore } from "@/stores/session-store";

const router = useRouter();

// Cell-render functions return HTML for in-table display. Strip it before sending values to
// the DataTables Buttons exporters so the CSV/Excel/PDF/Copy outputs contain plain text.
const stripHtml = (data: unknown): string =>
  typeof data === 'string' ? data.replace(/<[^>]*>/g, '') : String(data ?? '');

const tableOptions = {
  columns: [
    { data: 'organizationName', title: 'Organization', render: (d: string) => `<div class="fw-medium">${d}</div>` },
    { data: 'productName', title: 'Product', render: (d: string) => `<span class="badge bg-secondary">${d}</span>` },
    { data: 'contactEmail', title: 'Contact Email' },
    { data: 'subscribedDate', title: 'Subscribed', render: (d: string) => formatDate(d) },
    { data: 'status', title: 'Status', render: (d: string) => `<span class="badge rounded-pill ${getStatusClass(d)}">${d}</span>` },
    { data: 'lastRun', title: 'Last Run', render: (d: string | null) => d ? formatDate(d) : '<span class="text-muted">No runs yet</span>' },
    { data: 'totalRuns', title: 'Total Runs', render: (d: number) => d > 0 ? `<span class="badge bg-primary">${d}</span>` : '<span class="text-muted">0</span>' },
  ],
  layout: {
    topEnd: {
      buttons: [
        {
          extend: 'collection',
          text: '<i class="bi bi-download me-1"></i>Export',
          className: 'bb-btn-tertiary-cta btn-sm',
          buttons: [
            { extend: 'csv', text: 'CSV', exportOptions: { format: { body: stripHtml } } },
            { extend: 'excel', text: 'Excel', exportOptions: { format: { body: stripHtml } } },
            { extend: 'pdf', text: 'PDF', exportOptions: { format: { body: stripHtml } } },
            { extend: 'copy', text: 'Copy', exportOptions: { format: { body: stripHtml } } },
          ],
        },
      ],
    },
    bottomEnd: '',
    bottomStart: '',
  },
  language: {
    emptyTable: `
      <div class="text-center py-5">
        <i class="bi bi-people display-1 text-muted"></i>
        <h3 class="mt-3 text-muted">No subscribers yet</h3>
        <p class="text-muted mb-0">Once organizations subscribe to your products, they will appear here.</p>
      </div>
    `,
    zeroRecords: `
      <div class="text-center py-5">
        <i class="bi bi-search display-1 text-muted"></i>
        <h3 class="mt-3 text-muted">No subscribers found</h3>
        <p class="text-muted mb-0">Try adjusting your search filters.</p>
      </div>
    `,
  },
} as Config;

function getStatusClass(status: string): string {
  switch (status.toLowerCase()) {
    case "active":
      return "bg-success";
    case "paused":
      return "bg-warning";
    case "cancelled":
      return "bg-danger";
    default:
      return "bg-secondary";
  }
}

function viewSubscriberDetail(subscriberId: string) {
  router.push({
    name: "publisher-subscriber-detail",
    params: { subscriptionId: subscriberId }
  });
}

const subscribers = ref<any[]>([]);
const loading = ref(false);
const sessionStore = useSessionStore();
const { products } = useProducts();

// "My subscribers" = subscribers to products this org publishes. Filtering by ownership stops the
// page mixing in subscribers of products the org subscribes to (which `useProducts` returns too
// thanks to the visible-to-org union).
const publishedProducts = computed(() => {
  const myOrgId = sessionStore.session?.currentOrgId;
  if (!myOrgId) return [];
  return products.value.filter((p) => p.orgId === myOrgId);
});

onMounted(async () => {
  await loadAllSubscribers();
});

// Reload when component is reactivated (e.g., navigating back from detail view)
onActivated(async () => {
  await loadAllSubscribers();
});

const searchQuery = ref("");
const statusFilter = ref("all");
const productFilter = ref("all");

async function loadAllSubscribers() {
  try {
    loading.value = true;

    // Only iterate products this org publishes. `useProducts()` returns the visible-to-org union
    // (subscribed + owned), so without this filter we'd also try to load subscribers for products
    // the org subscribes to — wrong audience for the "my subscribers" page.
    const ownProducts = publishedProducts.value;
    const allSubscriptions: any[] = [];

    for (const product of ownProducts) {
      try {
        // Subscribers + per-org run metrics fetched in parallel — same pattern as SubscribersTab.
        const [productSubs, metrics] = await Promise.all([
          subscriptionsApi.getProductSubscribers(product.id),
          subscriptionsApi.getProductSubscriberMetrics(product.id),
        ]);
        const metricsByOrg = new Map(metrics.map((m) => [m.orgId, m]));

        for (const sub of productSubs) {
          const m = metricsByOrg.get(sub.orgId);
          allSubscriptions.push({
            id: sub.id,
            orgId: sub.orgId,
            organizationName: sub.orgName,
            contactEmail: sub.contactEmail,
            subscribedDate: sub.startDate,
            status: sub.isActive ? "Active" : "Paused",
            // null when no runs attributed yet — column renderer turns it into a muted
            // "No runs yet" string. Real ISO timestamp otherwise.
            lastRun: m?.lastRun ?? null,
            totalRuns: m?.totalRuns ?? 0,
            productName: product.name,
            productId: product.id,
          });
        }
      } catch (err) {
        console.error(`Failed to load subscribers for product ${product.id}:`, err);
      }
    }

    subscribers.value = allSubscriptions;
  } catch (error) {
    console.error("Failed to load subscribers:", error);
  } finally {
    loading.value = false;
  }
}

const filteredSubscribers = computed(() => {
  let filtered = subscribers.value;

  if (searchQuery.value) {
    filtered = filtered.filter(
      (subscriber) =>
        subscriber.organizationName
          .toLowerCase()
          .includes(searchQuery.value.toLowerCase()) ||
        subscriber.contactEmail
          .toLowerCase()
          .includes(searchQuery.value.toLowerCase())
    );
  }

  if (statusFilter.value !== "all") {
    filtered = filtered.filter(
      (subscriber) => subscriber.status.toLowerCase() === statusFilter.value
    );
  }

  if (productFilter.value !== "all") {
    filtered = filtered.filter(
      (subscriber) => subscriber.productName === productFilter.value
    );
  }

  return filtered;
});

const uniqueProducts = computed(() => {
  const products = [...new Set(subscribers.value.map((s) => s.productName))];
  return products;
});

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const showAddSubscriberModal = ref(false);

function addSubscriber() {
  showAddSubscriberModal.value = true;
}

async function handleSubscriberAdded() {
  await loadAllSubscribers();
}
</script>

<template>
  <div>
    <AddSubscriberWithProductModal
        v-model:show="showAddSubscriberModal"
        @subscribed="handleSubscriberAdded"
    />
    
    <PageHeader
      title="Subscribers"
      :breadcrumbs="[
        { label: 'Dashboard', href: '/' },
        { label: 'Publisher', href: '/publisher' },
        { label: 'Subscribers' }
      ]"
    >
      <template #actions>
        <button class="bb-btn-primary-cta" @click="addSubscriber">
          <i class="bi bi-plus me-2"></i>Add<span class="d-none d-md-inline"> Subscriber</span>
        </button>
      </template>
    </PageHeader>

    <!-- Summary Stats -->
    <div class="row g-3 mb-4">
      <div class="col-6 col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <div class="fs-3 fw-bold text-primary mb-1">{{ filteredSubscribers.length }}</div>
            <div class="text-muted small">Total Subscribers</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <div class="fs-3 fw-bold text-success mb-1">
              {{ filteredSubscribers.filter((s) => s.status === "Active").length }}
            </div>
            <div class="text-muted small">Active</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <div class="fs-3 fw-bold text-warning mb-1">
              {{ filteredSubscribers.filter((s) => s.status === "Paused").length }}
            </div>
            <div class="text-muted small">Paused</div>
          </div>
        </div>
      </div>
      <div class="col-6 col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <div class="fs-3 fw-bold text-info mb-1">
              {{ filteredSubscribers.reduce((sum, s) => sum + s.totalRuns, 0) }}
            </div>
            <div class="text-muted small">Total Runs</div>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-3 mb-4">
      <div class="col-md-4">
        <input
          type="text"
          class="form-control"
          placeholder="Search subscribers..."
          v-model="searchQuery"
        />
      </div>
      <div class="col-md-4">
        <select class="form-select" v-model="productFilter">
          <option value="all">All Products</option>
          <option
            v-for="product in uniqueProducts"
            :key="product"
            :value="product"
          >
            {{ product }}
          </option>
        </select>
      </div>
      <div class="col-md-4">
        <select class="form-select" v-model="statusFilter">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="pending">Pending</option>
        </select>
      </div>
    </div>

    <!-- Loading state — the page iterates every published product to fetch subscribers + metrics,
         which can take a moment. Without this branch the table briefly renders its empty-state
         template, reading as "no subscribers" before the data lands. -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-secondary" role="status">
        <span class="visually-hidden">Loading subscribers…</span>
      </div>
      <p class="text-muted small mt-3 mb-0">Loading subscribers…</p>
    </div>

    <DataView
      v-else
      :data="filteredSubscribers"
      view-mode="table"
      :options="tableOptions"
      @row-click="(row) => viewSubscriberDetail(row.id)"
    >
      <template #grid-card="{ row: subscriber }">
        <div class="card h-100 card-hover">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <h6 class="mb-0 fw-medium">{{ subscriber.organizationName }}</h6>
              <span class="badge rounded-pill" :class="getStatusClass(subscriber.status)">
                {{ subscriber.status }}
              </span>
            </div>
            <div class="mb-2">
              <span class="badge bg-secondary">{{ subscriber.productName }}</span>
            </div>
            <div class="small text-muted">
              <div><i class="bi bi-envelope me-1"></i>{{ subscriber.contactEmail }}</div>
              <div><i class="bi bi-calendar me-1"></i>Subscribed {{ formatDate(subscriber.subscribedDate) }}</div>
              <div><i class="bi bi-clock me-1"></i>Last run {{ subscriber.lastRun }}</div>
            </div>
          </div>
          <div class="card-footer bg-transparent border-top-0">
            <div class="d-flex justify-content-between align-items-center">
              <span class="badge bg-primary">{{ subscriber.totalRuns }} runs</span>
              <i class="bi bi-chevron-right text-muted"></i>
            </div>
          </div>
        </div>
      </template>
      <template #empty-state>
        <div class="text-center py-5">
          <i class="bi bi-people display-1 text-muted"></i>
          <h3 class="mt-3 text-muted">No subscribers yet</h3>
          <p class="text-muted">
            Once organizations subscribe to your products, they will appear here.
          </p>
          <button class="bb-btn-primary-cta mt-2" @click="addSubscriber">
            <i class="bi bi-plus-circle me-1"></i> Add Subscriber
          </button>
        </div>
      </template>
    </DataView>
  </div>
</template>

<style scoped lang="scss">
/* COMMENTED OUT: Redundant - Bootstrap already handles responsive grid behavior */
/* Force column widths to ensure proper layout - 2 per row on mobile */
/*@media (max-width: 767.98px) {*/
/*  .row .col-6 {*/
/*    flex: 0 0 50%;*/
/*    max-width: 50%;*/
/*  }*/
/*}*/

/*@media (min-width: 768px) {*/
/*  .row .col-md-3 {*/
/*    flex: 0 0 25%;*/
/*    max-width: 25%;*/
/*  }*/
/*}*/
</style>
